/**
 * Storefront-facing DTOs and Zod schemas for trade-account applications.
 *
 * This module is the canonical source for the application status / business
 * type value lists (`APPLICATION_STATUSES`, `BUSINESS_TYPES`).
 *
 * `packages/db/src/schema/customer-applications.ts` keeps PARALLEL `as const`
 * arrays — it does NOT import from here, because Drizzle requires literal
 * `as const` arrays at schema-definition time and we deliberately keep
 * `@wmart/shared-types` out of `@wmart/db`'s runtime deps. The DB file
 * documents the duplication in its own sync-obligation comment that points
 * back here.
 *
 * SYNC OBLIGATION — if you change the values in either array below, you MUST
 * also update, in the same PR:
 *   1. `customerApplicationStatusValues` /
 *      `customerApplicationBusinessTypeValues` in
 *      `packages/db/src/schema/customer-applications.ts`
 *   2. The `sql` template strings inside the two `check()` constraints in
 *      that same file (they list the values verbatim)
 *   3. A new Drizzle migration that drops + recreates the CHECK constraint
 *      on the live database
 *
 * Naming convention: camelCase throughout (L1 — storefront / applicant-facing).
 */
import { z } from "zod";
// ---------- Canonical enum value arrays (source of truth for the whole repo) ----------
/**
 * All possible trade-application statuses. Exported so the DB schema and any
 * future migration helpers can import instead of duplicating the literals.
 */
export const APPLICATION_STATUSES = [
    "pending",
    "approved",
    "rejected",
];
/**
 * All supported business types for a trade-account application. The DB schema
 * uses this array for its `text` column enum constraint. Add new types here
 * first, then generate a migration.
 */
export const BUSINESS_TYPES = [
    "corner_shop",
    "restaurant",
    "cafe",
    "hotel",
    "catering",
    "bakery",
    "wholesale_reseller",
    "other",
];
// ---------- Regex constants ----------
const PHONE_RE = /^[+0-9 ()-]{7,20}$/;
// Declared for consumer-side validation in the storefront ApplicationForm
// (P12-T11) to surface a postcode-specific hint for GB applicants. NOT wired
// into the Zod schema — per design spec §6.3 the API stays permissive
// ("UK pattern preferred but accept any non-empty for non-GB countries").
const UK_POSTCODE_RE = /^[A-Z]{1,2}[0-9][A-Z0-9]? ?[0-9][A-Z]{2}$/i;
// Matches standard EU/UK VAT numbers (e.g. GB123456789, DE123456789).
// EXCLUDES HMRC government-VAT formats like `GBGD###` / `GBHA###` — if a
// trade applicant supplies one of those, the storefront sign-up will reject
// it client-side; an exception path (admin manual entry / VAT-override
// field) is V1.5+ scope.
const VAT_RE = /^[A-Z]{2}[A-Z0-9]{7,12}$/i;
// ---------- Request schemas ----------
/**
 * Full sign-up payload submitted by a prospective trade customer when they
 * first register on the storefront. Validated server-side in the auth
 * controller (Plan 12-T4). `email` + `password` go to Supabase Auth; the
 * remaining fields populate `customer_applications`.
 */
export const CustomerSignUpInput = z.object({
    email: z.email(),
    password: z.string().min(8),
    contactFullName: z.string().min(1).max(100),
    contactPhone: z.string().regex(PHONE_RE, "Invalid phone format"),
    businessLegalName: z.string().min(1).max(200),
    businessType: z.enum(BUSINESS_TYPES),
    vatNumber: z.string().regex(VAT_RE).optional().nullable(),
    addressLine1: z.string().min(1).max(200),
    addressLine2: z.string().max(200).optional().nullable(),
    addressCity: z.string().min(1).max(100),
    addressPostcode: z.string().min(1).max(20),
    addressCountry: z.string().length(2).default("GB"),
});
/**
 * Subset of `CustomerSignUpInput` used when an applicant updates a pending
 * application (PUT /v1/storefront/me/application). Credentials are excluded —
 * email changes go through Supabase Auth, password reset through its own flow.
 */
export const UpdateMyApplicationInput = CustomerSignUpInput.omit({
    email: true,
    password: true,
});
// ---------- UI helpers ----------
/**
 * Human-readable labels for each business type. Used by storefront forms and
 * the admin detail panel to render `businessType` values without hardcoding
 * labels in components.
 */
export const BUSINESS_TYPE_LABELS = {
    corner_shop: "Corner shop / convenience",
    restaurant: "Restaurant",
    cafe: "Café",
    hotel: "Hotel",
    catering: "Catering",
    bakery: "Bakery",
    wholesale_reseller: "Wholesale reseller",
    other: "Other",
};
/**
 * Explicit alias for `BUSINESS_TYPES` aimed at call sites (e.g. storefront
 * form select) that prefer the more descriptive name when iterating the
 * array. Equivalent to `BUSINESS_TYPES` — kept as a separate export so
 * consumers can pick whichever name reads better in context.
 */
export const BUSINESS_TYPES_ARRAY = BUSINESS_TYPES;
